import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Input,
  Selection,
  Select,
  SelectItem,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Autocomplete,
  AutocompleteItem,
  Image,
  Tooltip,
  Modal,
  ModalContent,
  useDisclosure,
  ScrollShadow,
  Divider,
  CardFooter,
} from "@heroui/react";
import { FiSearch } from "react-icons/fi";
import { useSubmit, Link, useLocation, useSearchParams } from "@remix-run/react";
import { useQuery } from "@tanstack/react-query";
import { colors } from "app/utils/searchColors";
import { toKebabCase } from "app/utils/fn";
import { fetchProductsTextSearchApi } from "app/api/product/products.api";
import { useSafeIsMobile } from "app/utils/deviceDetection";
import { MdOutlineSettings } from "react-icons/md";

const sortFilter = [
  { value: "lowest-price", label: "Price, lowest first" },
  { value: "highest-price", label: "Price, highest first" },
  {
    label: "Alphabetical (A-Z)",
    value: "A-Z",
  },
  {
    label: "Alphabetical (Z-A)",
    value: "Z-A",
  },
  { value: "recently added", label: "Recently Added" },
];

export const SearchDropdown = () => {
  const isMobile = useSafeIsMobile();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isOpen, onOpenChange, onClose } = useDisclosure();
  const [isPopOpen, setIsPopOpen] = useState(false);

  const [priceSort, setPriceSort] = React.useState<Selection>(new Set([]));
  const [search, setSearchValue] = useState<string>("");
  const [colours, setSearchColours] = useState<string[]>([]);
  const [minPrice, setMinPrice] = useState<string>(searchParams.get("minPrice") || "");
  const [maxPrice, setMaxPrice] = useState<string>(searchParams.get("maxPrice") || "");
  const [minQty, setMinQty] = useState<string>(searchParams.get("minQty") || "");
  const [priceError, setPriceError] = useState<string>("");
  const submit = useSubmit();
  const [fieldState, setFieldState] = React.useState<any>({
    selectedKey: null,
    inputValue: "",
  });
  // Add state to track when search should be triggered
  const [shouldTriggerSearch, setShouldTriggerSearch] = useState<boolean>(false);
  // const buttonRef = React.useRef<HTMLButtonElement>(null);
  const autocompleteRef = React.useRef<HTMLDivElement>(null);
  // Refs for advanced search modal input wrappers (iOS fix)
  const quantityInputWrapperRef = React.useRef<HTMLDivElement>(null);
  const minPriceInputWrapperRef = React.useRef<HTMLDivElement>(null);
  const maxPriceInputWrapperRef = React.useRef<HTMLDivElement>(null);

  const {
    data: products,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: ["searchdProducts", search, shouldTriggerSearch],
    queryFn: () =>
      fetchProductsTextSearchApi({ search: search?.trim(), normalSearch: "true", limit: 10 }),
    enabled: shouldTriggerSearch && !!search?.trim(),
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  // Initialize colours from URL params
  useEffect(() => {
    const urlColours = searchParams.getAll("colours");
    if (urlColours.length > 0) {
      setSearchColours(urlColours);
    }
  }, []);

  // Initialize price sort from URL params
  useEffect(() => {
    const sort = searchParams.get("sort");
    if (sort) {
      setPriceSort(new Set([sort]));
    }
  }, []);

  // Helper function to blur the input field (needed for iOS keyboard dismissal)
  const blurSearchInput = useCallback(() => {
    // Function to perform the blur
    const performBlur = () => {
      // Try to find and blur the input element within the Autocomplete
      if (autocompleteRef.current) {
        const input = autocompleteRef.current.querySelector("input");
        if (input) {
          input.blur();
          return true;
        }
      }
      // Fallback: blur the currently active element if it's an input
      if (
        document.activeElement &&
        (document.activeElement.tagName === "INPUT" ||
          document.activeElement.tagName === "TEXTAREA")
      ) {
        (document.activeElement as HTMLElement).blur();
        return true;
      }
      return false;
    };

    // Try to blur immediately
    performBlur();

    // Also try after a small delay (iOS sometimes needs this)
    setTimeout(() => {
      performBlur();
    }, 100);
  }, []);

  // Helper function to handle iOS input focus (fixes double-tap issue)
  const handleIOSInputFocus = useCallback(
    (wrapperRef: React.RefObject<HTMLDivElement>) => {
      return (e: React.TouchEvent) => {
        // Only apply fix on mobile devices
        if (isMobile && wrapperRef.current) {
          // Find the input element within the wrapper
          const input = wrapperRef.current.querySelector("input");
          if (input) {
            // Prevent default to avoid double-tap
            e.preventDefault();
            // Focus the input immediately
            setTimeout(() => {
              input.focus();
              // Ensure cursor is visible by setting selection
              const length = input.value.length;
              input.setSelectionRange(length, length);
            }, 0);
          }
        }
      };
  },
    [isMobile],
  );

  const onSearchChange = useCallback(
    (value?: string) => {
      setSearchValue(value || "");
      // Reset search trigger when search text changes
      if (value !== search) {
        setShouldTriggerSearch(false);
      }
    },
    [search],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();

        // Update URL parameters with current filter values
        const newParams = new URLSearchParams(searchParams);
        if (search) newParams.set("q", search?.trim());
        const price = Array.from(priceSort)[0]?.toString();
        if (price) newParams.set("sort", price);
        if (minQty) newParams.set("minQty", minQty);
        if (minPrice) newParams.set("minPrice", minPrice);
        if (maxPrice) newParams.set("maxPrice", maxPrice);
        if (colours.length > 0) {
          newParams.delete("colours");
          colours.forEach((colour) => newParams.append("colours", colour));
        }
        setSearchParams(newParams);

        const formData = new FormData();
        if (search) {
          formData.set("q", search?.trim());
        }
        if (price) formData.set("sort", price);
        if (minQty) formData.set("minQty", minQty);
        if (minPrice) formData.set("minPrice", minPrice);
        if (maxPrice) formData.set("maxPrice", maxPrice);
        if (colours.length > 0) {
          colours.forEach((colour) => formData.append("colours", colour));
        }

        // Reset search trigger state before submitting
        setShouldTriggerSearch(false);

        // Close any open popovers/modals
        setIsPopOpen(false);
        onClose();

        // Update field state
        setFieldState(() => {
          return {
            inputValue: search,
            selectedKey: search,
          };
        });

        // Blur the input to dismiss keyboard on iOS/iPadOS
        blurSearchInput();

        // Submit the search
        submit(formData, { method: "get", action: "/search" });
      }
    },
    [
      search,
      submit,
      searchParams,
      setSearchParams,
      priceSort,
      minQty,
      minPrice,
      maxPrice,
      colours,
      onClose,
      blurSearchInput,
    ],
  );

  const onSelectionChange = useCallback(
    (key: React.Key | null) => {
      if (products?.products) {
        setFieldState(() => {
          let selectedItem = products?.products.find((option) => option.slug === key);

          return {
            inputValue: selectedItem?.overview.name || "",
            selectedKey: key,
          };
        });
        // Don't trigger search when selecting from dropdown
        setShouldTriggerSearch(false);

        // Blur the input to dismiss keyboard on iOS/iPadOS when selecting a product
        blurSearchInput();
      }
    },
    [products?.products, blurSearchInput],
  );

  const handleSubmit = useCallback(
    (e: any) => {
      if ("preventDefault" in e) e.preventDefault();
      if (priceError) return;

      // Update URL parameters with current filter values
      const newParams = new URLSearchParams(searchParams);
      if (search) newParams.set("q", search?.trim());
      const price = Array.from(priceSort)[0]?.toString();
      if (price) newParams.set("sort", price);
      if (minQty) newParams.set("minQty", minQty);
      if (minPrice) newParams.set("minPrice", minPrice);
      if (maxPrice) newParams.set("maxPrice", maxPrice);
      if (colours.length > 0) {
        newParams.delete("colours");
        colours.forEach((colour) => newParams.append("colours", colour));
      }
      setSearchParams(newParams);

      const formData = new FormData();
      if (search) formData.set("q", search?.trim());
      if (price) formData.set("sort", price);
      if (minQty) formData.set("minQty", minQty);
      if (minPrice) formData.set("minPrice", minPrice);
      if (maxPrice) formData.set("maxPrice", maxPrice);
      if (colours.length > 0) {
        colours.forEach((colour) => formData.append("colours", colour));
      }
      setIsPopOpen(false);
      onClose();

      // Reset the search trigger state
      setShouldTriggerSearch(false);

      // a hack used to close the popup
      // buttonRef.current?.click();
      // buttonRef.current?.focus();
      // buttonRef.current?.blur();
      setFieldState(() => {
        return {
          inputValue: search,
          selectedKey: search,
        };
      });

      // Blur the input to dismiss keyboard on iOS/iPadOS
      blurSearchInput();

      submit(formData, { method: "get", action: "/search" });
    },
    [
      search,
      minPrice,
      maxPrice,
      minQty,
      priceSort,
      colours,
      onClose,
      submit,
      searchParams,
      setSearchParams,
      blurSearchInput,
    ],
  );

  const handleSelectColour = useCallback(
    (col: string) => {
      if (colours.includes(col)) {
        setSearchColours((prevCols) => prevCols.filter((cc) => cc !== col));
      } else if (colours.length < 6) {
        setSearchColours((prevCols) => [...prevCols, col]);
      }
      // Don't update URL params until search is clicked
    },
    [colours],
  );

  const handleResetFilter = useCallback(() => {
    setSearchColours([]);
    setMinQty("");
    setMaxPrice("");
    setMinPrice("");
    setPriceSort(new Set());
    setShouldTriggerSearch(false);
    setPriceError(""); // Clear price error when resetting
    // Clear URL params when resetting
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("colours");
    newParams.delete("minQty");
    newParams.delete("minPrice");
    newParams.delete("maxPrice");
    newParams.delete("sort");
    setSearchParams(newParams);
    onClose();
    setIsPopOpen(false);
  }, [onClose, searchParams, setSearchParams]);

  const handleMinQty = useCallback((n: string) => {
    setMinQty(n);
  }, []);

  const handleMinPriceChange = (value: string) => {
    setMinPrice(value);

    // Clear error if either value is empty
    if (!value || !maxPrice) {
      setPriceError("");
      return;
    }

    // Check validation only when both values exist
    if (parseFloat(value) >= parseFloat(maxPrice)) {
      setPriceError("Minimum price must be less than maximum price");
    } else {
      setPriceError("");
    }
  };

  const handleMaxPriceChange = (value: string) => {
    setMaxPrice(value);

    // Clear error if either value is empty
    if (!value || !minPrice) {
      setPriceError("");
      return;
    }

    // Check validation only when both values exist
    if (parseFloat(minPrice) >= parseFloat(value)) {
      setPriceError("Maximum price must be greater than minimum price");
    } else {
      setPriceError("");
    }
  };

  const handleSearchButtonClick = useCallback(() => {
    if (search?.trim()) {
      // Update URL parameters with current filter values
      const newParams = new URLSearchParams(searchParams);
      if (search) newParams.set("q", search?.trim());
      const price = Array.from(priceSort)[0]?.toString();
      if (price) newParams.set("sort", price);
      if (minQty) newParams.set("minQty", minQty);
      if (minPrice) newParams.set("minPrice", minPrice);
      if (maxPrice) newParams.set("maxPrice", maxPrice);
      if (colours.length > 0) {
        newParams.delete("colours");
        colours.forEach((colour) => newParams.append("colours", colour));
      }
      setSearchParams(newParams);

      setShouldTriggerSearch(true);
      refetch();
    }
  }, [
    search,
    refetch,
    searchParams,
    setSearchParams,
    priceSort,
    minQty,
    minPrice,
    maxPrice,
    colours,
  ]);

  // Debounce search refetch - only trigger when search text changes
  const debounceTime = 800;
  useEffect(() => {
    const handler = setTimeout(() => {
      if (search) {
        setShouldTriggerSearch(true);
        refetch();
      }
    }, debounceTime);
    return () => clearTimeout(handler);
  }, [refetch, search]);

  // Auto-clear price error when values become valid
  useEffect(() => {
    if (minPrice && maxPrice && parseFloat(minPrice) < parseFloat(maxPrice)) {
      setPriceError("");
    }
  }, [minPrice, maxPrice]);

  const popContent = useMemo(
    () => (
      <Popover
        isOpen={isPopOpen}
        onOpenChange={(open) => setIsPopOpen(open)}
        placement="bottom-start"
        backdrop="blur"
        isDismissable={true}
        isKeyboardDismissDisabled={true}
      >
        <PopoverTrigger>
          <Button
            // onPress={() => setIsPopOpen(true)}
            isIconOnly
            variant="light"
            aria-label="Advanced search settings"
          >
            <MdOutlineSettings className="w-6 h-6" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-1 rounded-lg border-2 dark:border-gray-400">
          <Card shadow="none" radius="none" className="border-none bg-transparent">
            <CardBody className="p-3">
              <div className="flex flex-col md:flex-row space-x-4 max-w-[45rem]">
                <div className="space-y-4 p-4">
                  <div className="grid grid-cols-1 gap-3">
                    <div className="space-y-1">
                      <Input
                        type="number"
                        inputMode="numeric"
                        label="Quantity"
                        labelPlacement="outside"
                        placeholder="100"
                        variant="bordered"
                        value={minQty}
                        min="0"
                        onValueChange={handleMinQty}
                        classNames={{
                          inputWrapper: ["border-zinc-500"],
                          label: "font-medium",
                          input: "text-base sm:text-sm",
                        }}
                        aria-label="Minimum quantity"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Input
                        type="number"
                        step="0.01"
                        inputMode="decimal"
                        label="Price from"
                        labelPlacement="outside"
                        placeholder="10.00"
                        startContent="$"
                        variant="bordered"
                        value={minPrice}
                        min="0"
                        onValueChange={handleMinPriceChange}
                        classNames={{
                          inputWrapper: ["border-zinc-500"],
                          label: "font-medium",
                          input: "text-base sm:text-sm",
                        }}
                        aria-label="Minimum price"
                      />
                      <span className="text-xs text-gray dark:text-gray-100 italic">
                        Plus Branding and Freight cost
                      </span>
                    </div>
                    <div className="space-y-1">
                      <Input
                        type="number"
                        step="0.01"
                        inputMode="decimal"
                        label="Price To"
                        labelPlacement="outside"
                        placeholder="12.50"
                        startContent="$"
                        variant="bordered"
                        value={maxPrice}
                        min="0"
                        onValueChange={handleMaxPriceChange}
                        classNames={{
                          inputWrapper: ["border-zinc-500"],
                          label: "font-medium",
                          input: "text-base sm:text-sm",
                        }}
                        aria-label="Maximum price"
                      />
                    </div>
                  </div>
                  {priceError && <div className="text-red-500 text-sm mt-1">{priceError}</div>}
                  <div className="flex flex-col gap-2">
                    <Select
                      label="Sort results by:"
                      labelPlacement="outside"
                      placeholder="Price, lowest first"
                      selectedKeys={priceSort}
                      className="w-full"
                      onSelectionChange={setPriceSort}
                      variant="bordered"
                      classNames={{
                        base: "border-zinc-red",
                        value: "border-zinc-red",
                        mainWrapper: "border-zinc-red",
                        listboxWrapper: "border-zinc-red",
                        label: "font-medium",
                      }}
                      aria-label="Sort results"
                    >
                      {sortFilter.map((ss) => (
                        <SelectItem key={ss.value}>{ss.label}</SelectItem>
                      ))}
                    </Select>
                  </div>
                  <div className="flex items-center gap-4 justify-end">
                    <Button
                      onPress={handleResetFilter}
                      variant="flat"
                      color="primary"
                      className="px-4"
                      aria-label="Reset filters"
                    >
                      Reset
                    </Button>
                    <Button
                      onPress={handleSubmit}
                      className="bg-primary text-white px-4"
                      aria-label="Search"
                      isDisabled={!!priceError}
                    >
                      Search
                    </Button>
                  </div>
                </div>
                <div className="space-y-4 p-4">
                  <h3 className="text-lg font-semibold">Search by Colours</h3>
                  <span className="text-sm text-gray dark:text-foreground font-normal">
                    Select one or more colours to highlight products available
                  </span>
                  <div className="grid grid-cols-5 gap-3 w-full bg-zinc-100 dark:bg-zinc-700 p-4 rounded-lg">
                    {colors.map((c, i) => (
                      <Tooltip
                        content={
                          !colours.includes(c) && colours.length >= 6
                            ? "Maximum 6 colors can be selected"
                            : c
                        }
                        key={`${i}-${c}`}
                      >
                        <Button
                          onPress={() => handleSelectColour(c)}
                          isIconOnly
                          className={
                            colours.includes(c)
                              ? "w-11 h-11 aspect-square rounded-full shadow-xl border-2 border-white bg-overlay-black transform scale-105 transition-transform"
                              : "w-10 h-10 aspect-square rounded-full dark:border dark:border-zinc-500"
                          }
                          style={{
                            backgroundColor: c,
                            outline: colours.includes(c) ? `1px dashed ${c}` : "none",
                            opacity: !colours.includes(c) && colours.length >= 6 ? 0.4 : 1,
                          }}
                          aria-label={`Select colour ${c}`}
                        />
                      </Tooltip>
                    ))}
                  </div>
                  <div>
                    {colours.length > 0 && (
                      <span className="pt-2" aria-live="polite">
                        Selected Colours: {colours.join(", ")}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </PopoverContent>
      </Popover>
    ),
    [
      isPopOpen,
      colours,
      priceSort,
      search,
      minQty,
      minPrice,
      maxPrice,
      handleResetFilter,
      handleMinQty,
      handleSubmit,
      handleSelectColour,
      handleMinPriceChange,
      handleMaxPriceChange,
      priceError,
    ],
  );

  return (
    <div className="w-full">
      <Modal
        placement="top"
        backdrop="blur"
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        hideCloseButton={true}
        isDismissable={true}
        isKeyboardDismissDisabled={true}
        motionProps={{
          variants: {
            enter: {
              y: 0,
              opacity: 1,
              transition: {
                duration: 0.3,
                ease: "easeOut",
              },
            },
            exit: {
              y: -20,
              opacity: 0,
              transition: {
                duration: 0.2,
                ease: "easeIn",
              },
            },
          },
        }}
        aria-labelledby="advanced-search-modal"
      >
        <ModalContent className="p-1 rounded-lg border border-foreground-200">
          <Card shadow="none" radius="none" className="border-none bg-transparent p-0">
            <CardBody className="p-0">
              <div className="text-gray dark:text-foreground font-semibold px-3 py-2">
                Advanced Search
              </div>
              <ScrollShadow
                className="w-full bg-zinc-100 dark:bg-zinc-800 rounded-xl py-3 px-4"
                orientation="horizontal"
                hideScrollBar={false}
              >
                <div className="flex gap-2 w-full">
                  {colors.map((c, i) => (
                    <Tooltip content={c} key={`${i}-${c}`}>
                      <Button
                        onPress={() => handleSelectColour(c)}
                        isIconOnly
                        className={
                          colours.includes(c)
                            ? "w-12 h-12 p-1 aspect-square rounded-full shadow-lg border-4 border-white bg-overlay-black transform scale-110 transition-transform"
                            : "w-10 h-10 aspect-square rounded-full dark:border-2 dark:border-zinc-500"
                        }
                        style={{ backgroundColor: c }}
                        aria-label={`Select colour ${c}`}
                      />
                    </Tooltip>
                  ))}
                </div>
              </ScrollShadow>
              <Divider className="mt-3 mb-2" />
              <div className="p-3">
                <div className="grid gap-3 mb-3">
                  <div
                    ref={quantityInputWrapperRef}
                    className="space-y-1"
                    onTouchStart={handleIOSInputFocus(quantityInputWrapperRef)}
                  >
                    <Input
                      type="number"
                      inputMode="numeric"
                      placeholder="Quantity"
                      className="text-primary"
                      variant="bordered"
                      value={minQty}
                      min="0"
                      onValueChange={handleMinQty}
                      classNames={{
                        inputWrapper: ["border-zinc-500"],
                        label: "font-medium",
                        input: "text-base sm:text-sm",
                      }}
                      aria-label="Quantity"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div
                    ref={minPriceInputWrapperRef}
                    className="space-y-1"
                    onTouchStart={handleIOSInputFocus(minPriceInputWrapperRef)}
                  >
                    <Input
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      placeholder="Price from"
                      className="text-primary"
                      startContent="$"
                      variant="bordered"
                      value={minPrice}
                      min="0"
                      onValueChange={handleMinPriceChange}
                      classNames={{
                        inputWrapper: ["border-zinc-500"],
                        label: "font-medium",
                        input: "text-base sm:text-sm",
                      }}
                      aria-label="Price from"
                    />
                    <span className="text-xs text-gray dark:text-gray-100">
                      Plus Branding and Freight cost{" "}
                    </span>
                  </div>
                  <div
                    ref={maxPriceInputWrapperRef}
                    className="space-y-1"
                    onTouchStart={handleIOSInputFocus(maxPriceInputWrapperRef)}
                  >
                    <Input
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      placeholder="Price To"
                      className="text-primary"
                      startContent="$"
                      variant="bordered"
                      value={maxPrice}
                      min="0"
                      onValueChange={handleMaxPriceChange}
                      classNames={{
                        inputWrapper: ["border-zinc-500"],
                        label: "font-medium",
                        input: "text-base sm:text-sm",
                      }}
                      aria-label="Price to"
                    />
                  </div>
                </div>
                {priceError && <div className="text-red-500 text-sm mt-1">{priceError}</div>}
                <div className="flex flex-col gap-2">
                  <Select
                    placeholder="Price, lowest first"
                    selectedKeys={priceSort}
                    className="w-full"
                    onSelectionChange={setPriceSort}
                    classNames={{
                      base: "border-zinc-red",
                      value: "border-zinc-red",
                      mainWrapper: "border-zinc-red",
                      listboxWrapper: "border-zinc-red",
                      label: "font-medium",
                    }}
                    aria-label="Sort results"
                  >
                    {sortFilter.map((ss) => (
                      <SelectItem key={ss.value}>{ss.label}</SelectItem>
                    ))}
                  </Select>
                </div>
              </div>
            </CardBody>
            <CardFooter className="gap-2 justify-end">
              <Button
                onPress={onClose}
                variant="flat"
                color="primary"
                className="px-4"
                aria-label="Close advanced search"
              >
                Close
              </Button>
              <Button
                onPress={handleSubmit}
                className="bg-primary text-white px-4"
                aria-label="Submit search"
                isDisabled={!!priceError}
              >
                Search
              </Button>
            </CardFooter>
          </Card>
        </ModalContent>
      </Modal>
      <form
        onSubmit={(e) => e.preventDefault()}
        role="search"
        className="w-full"
        onKeyDown={handleKeyDown}
      >
        <div className="flex items-center" ref={autocompleteRef}>
          <Autocomplete
            showScrollIndicators={true}
            // menuTrigger={menuTrigger}
            inputValue={search}
            selectedKey={fieldState.selectedKey}
            isLoading={isLoading}
            scrollShadowProps={{ isEnabled: false }}
            items={
              products && products?.products && products?.products.length > 0
                ? products?.products
                : []
            }
            size="lg"
            variant="bordered"
            placeholder="Search product catalogue"
            radius="none"
            className="rounded-s-sm"
            classNames={{
              listbox: ["border", "border-zinc-100"],
              popoverContent: ["rounded-md"],
              listboxWrapper: ["rounded-md", "show-box-scrollbar"],
            }}
            clearButtonProps={{ onPress: () => onSearchChange("") }}
            popoverProps={{
              isDismissable: true,
              isKeyboardDismissDisabled: true,

              classNames: {
                content: "border border-gray-200 dark:border-gray-500 dark:bg-gray-800",
              },
            }}
            listboxProps={{
              emptyContent: isLoading ? "Searching..." : "No results found.",

              // sc: true,
              // classNames: { list: "max-h-[280px] overflow-y-auto" },

              bottomContent:
                products && products?.products && products?.products.length >= 10 ? (
                  <div className="m-auto py-2 ">
                    <Button
                      size="sm"
                      onPress={handleSubmit}
                      variant="flat"
                      color="primary"
                      aria-label="View all search results"
                    >
                      View All Results
                    </Button>
                  </div>
                ) : null,
            }}
            startContent={
              isMobile ? (
                <Button
                  isIconOnly
                  variant="light"
                  onPress={onOpenChange}
                  aria-label="Open advanced search"
                >
                  <MdOutlineSettings className="w-6 h-6" />
                </Button>
              ) : (
                popContent
              )
            }
            // onValueChange={onSearchChange}
            onInputChange={onSearchChange}
            onSelectionChange={onSelectionChange}
            onKeyDown={handleKeyDown}
            aria-label="Search products"
          >
            {(item) => (
              <AutocompleteItem key={item.slug} className="capitalize">
                <Link
                  to={`/category/${
                    item.globalProductCategory.name
                      ? toKebabCase(item.globalProductCategory.name)
                      : "_"
                  }/${
                    item.globalProductSubCategory.name
                      ? toKebabCase(item.globalProductSubCategory.name)
                      : "_"
                  }/${item.slug}`}
                  aria-label={`Go to ${item.overview.name} details`}
                >
                  <div className="flex gap-4 items-center">
                    <Image
                      alt={`Image of ${item.overview.name}`}
                      src={item.overview.heroImage}
                      removeWrapper
                      height={48}
                      width={48}
                      className="!object-scale-down border-2 border-zinc-100"
                      classNames={{
                        wrapper:
                          "!object-scale-down border-2 border-zinc-100 h-12 w-12 transition aspect-square",
                      }}
                      loading="lazy"
                    />
                    <span>{item.overview.name}</span>
                    <span>{item.overview.code}</span>
                  </div>
                </Link>
              </AutocompleteItem>
            )}
          </Autocomplete>
          <Button
            type="button"
            size="lg"
            isIconOnly
            className="bg-primary text-white rounded-s-none rounded-e-md"
            aria-label="Search"
            isDisabled={!!priceError}
            onPress={handleSearchButtonClick}
          >
            <FiSearch />
          </Button>
        </div>
        {colours.length > 0 && (
          <div className="flex gap-2 mt-1" aria-live="polite">
            {colours.map((c, i) => (
              <div
                key={i}
                className="w-12 h-4 rounded-md shadow-md border-3 border-white"
                style={{ backgroundColor: c }}
                aria-label={`Selected colour ${c}`}
              />
            ))}
          </div>
        )}
      </form>
      {/* <button
        ref={buttonRef}
        hidden={true}
        className="w-0 h-0 border-none outline-none absolute top-0"
        aria-hidden="true"
        tabIndex={-1}
      /> */}
    </div>
  );
};
